import { Injectable, Injector, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpHeaders, HttpErrorResponse, HttpRequest, HTTP_INTERCEPTORS, HttpResponse } from '@angular/common/http';
import { AppService } from 'src/app/app.service';
import { gitTag } from 'src/environments/version'; 
import { JWTTokenService } from '../app.jwt-token.service';
import { PlanGateService } from '../services/plan-gate.service';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {

    autoRefreshInProgress = false;
    version: any;

    constructor(
        private appService: AppService,
        private router: Router,
        private jwtservice: JWTTokenService,
        private injector: Injector
    ) { }

    /** Resolved lazily: PlanGateService pulls in AppService, which owns the
     *  HttpClient this interceptor is attached to. */
    private get planGate(): PlanGateService {
        return this.injector.get(PlanGateService);
    }

    /** 403 "Plan is not active" is a billing state, not a bad session - it must
     *  not fall into the sign-the-user-out branch below. */
    private isPlanBlock(err: HttpErrorResponse): boolean {
        const message = `${err?.error?.message || ''} ${err?.error?.details?.reason || ''}`.toLowerCase();
        return message.includes('plan is not active')
            || message.includes('monthly limit')
            || message.includes('limit reached');
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req.clone({})).pipe(tap(
            (event: HttpEvent<any>) => {
            },
            async err => {
                if (err instanceof HttpErrorResponse){
                    if (err?.error?.code == 'NO_OAUTH_FOUND'){
                        return
                    } 
                }
                // Out of allowance. Lock the app down and send them to billing
                // instead of letting them hit this wall screen by screen.
                if (err instanceof HttpErrorResponse && err.status === 429) {
                    this.appService.addErrorMessage(err.error?.message || 'Monthly limit reached');
                    this.planGate.reportQuotaError(err.error);
                    return;
                }
                if (err instanceof HttpErrorResponse && err.status === 403 && this.isPlanBlock(err)) {
                    this.appService.addErrorMessage(err.error?.message || 'Your plan is not active');
                    this.planGate.reportQuotaError(err.error);
                    return;
                }
                if (err instanceof HttpErrorResponse && [401, 403].includes(err.status)) {
                    this.appService.addErrorMessage(err.error?.message)
                    const emailId = this.appService.get("xdbc.emailId");
                    if (emailId) {
                      this.appService.removeDevice(emailId);
                    }
                    this.appService.remove('xdbc.lastUrl');
                    this.appService.logOut()
                     .then(() => {
                            console.log('Calling handleTokenInvalidation from Interceptor...');
                            return Promise.resolve(this.jwtservice.handleTokenInvalidation());
                        })
                        .then(() => {
                            console.log('handleTokenInvalidation finished from Interceptor...');
                            return Promise.resolve(this.jwtservice.clearTokenExpirationCheck());
                        })              
                        .then(() => {
                            console.log('signOut finished from Interceptor...');
                        });
                } else if (err instanceof HttpErrorResponse && err.status == 502) {
                    this.appService.addErrorMessage('Server Unreachable')
                } else if (err instanceof HttpErrorResponse && err.status == 503) {
                    this.appService.addErrorMessage('Server Unreachable')
                } else if (err instanceof HttpErrorResponse && err.status == 504) {
                    this.appService.addErrorMessage('Server Unreachable')
                }
                else {
                    this.appService.addErrorMessage(err.error?.message)
                }
            }));
    }

    showVersionMismatchToast() {
        this.appService.addWarnMessage("A new version is available. This page will auto-refresh in 15 seconds.");
    }

    autoRefresh() {
        setTimeout(() => {
            location.reload();
        }, 15000);
    }

    getVersion() {
        let count = gitTag.version.split("-").length - 1;
        if (count === 2) {
            this.version = gitTag.version.split("-", 2).join("-");
        } else if (count === 1) {
            this.version = gitTag.version.split("-", 1).join("-");
        }
    }
}

@NgModule({
    imports: [
        CommonModule,
    ],
    providers: [{ provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true },
        AppService],
    declarations: []
})
export class InterceptorModule { }